import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { getPreferences, updatePreferences } from "../services/userPreferenceService";
import { UserPreferenceDto, UserPreferenceUpdateRequest } from "../models/UserPreferenceModel";

interface PreferencesContextType {
  preferences: UserPreferenceDto | null;
  loading: boolean;
  error: string | null;
  updateUserPreferences: (updates: UserPreferenceUpdateRequest) => Promise<void>;
  refreshPreferences: () => Promise<void>;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

const PREFERENCES_STORAGE_KEY = "user-preferences";

interface PreferencesProviderProps {
  children: ReactNode;
}

export const PreferencesProvider: React.FC<PreferencesProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferenceDto | null>(() => {
    // Load from localStorage on init for faster initial render
    const saved = localStorage.getItem(PREFERENCES_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load preferences from backend when user logs in
  const loadPreferences = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setPreferences(null);
      localStorage.removeItem(PREFERENCES_STORAGE_KEY);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getPreferences();
      setPreferences(data);
      localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      console.error("Failed to load preferences:", err);
      setError("Failed to load preferences");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Load preferences when auth state changes
  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  // Update preferences
  const updateUserPreferences = useCallback(async (updates: UserPreferenceUpdateRequest) => {
    try {
      setLoading(true);
      const updated = await updatePreferences(updates);
      setPreferences(updated);
      localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(updated));
    } catch (err) {
      console.error("Failed to update preferences:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh preferences from backend
  const refreshPreferences = useCallback(async () => {
    await loadPreferences();
  }, [loadPreferences]);

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        loading,
        error,
        updateUserPreferences,
        refreshPreferences,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = (): PreferencesContextType => {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error("usePreferences must be used within a PreferencesProvider");
  }
  return context;
};